<script setup>
import { computed, ref } from "vue"
import { api, post } from "../api"

const props = defineProps({
  partnerId: { type: Number, required: true },
  links: { type: Array, default: () => [] },
  tasks: { type: Array, default: () => [] }
})
const emit = defineEmits(["refresh"])
const query = ref("")
const results = ref([])
const selected = ref(null)
const kind = ref("related")
const relationType = ref("CrmTask")
const searching = ref(false)
const saving = ref(false)
let searchTimer

const taskRows = computed(() => props.tasks.map(task => ({ key: `task-${task.id}`, type: "CrmTask", id: task.id, title: task.title, subtitle: `${task.stage.name} · ${task.assignee.name}`, label: "Задача", path: `/tasks/${task.id}` })))
const partnerRows = computed(() => props.links.filter(link => link.entity.type === "Partner").map(link => ({ key: `link-${link.id}`, type: "Partner", id: link.entity.id, title: link.entity.title, subtitle: link.entity.subtitle, label: relation(link), path: `/partners/${link.entity.id}`, linkId: link.id })))
const rows = computed(() => [ ...taskRows.value, ...partnerRows.value ])

function relation(link) {
  if (link.kind === "related") return link.entity.type === "Partner" ? "Партнёр" : "Задача"
  return link.direction === "outgoing" ? "Блокирует" : "Заблокирована"
}

function scheduleSearch() {
  selected.value = null
  window.clearTimeout(searchTimer)
  if (!query.value.trim()) { results.value = []; return }
  searchTimer = window.setTimeout(search, 250)
}

function changeType(type) {
  relationType.value = type
  query.value = ""
  results.value = []
  selected.value = null
}

async function search() {
  const value = query.value.trim()
  if (!value) return
  searching.value = true
  try {
    const linked = new Set(rows.value.map(row => `${row.type}:${row.id}`))
    results.value = (await api(`/entities?type=${relationType.value}&q=${encodeURIComponent(value)}`))
      .filter(item => !(item.type === "Partner" && item.id === props.partnerId))
      .filter(item => !linked.has(`${item.type}:${item.id}`))
  } finally {
    searching.value = false
  }
}

function select(item) {
  selected.value = item
  query.value = item.title
  results.value = []
}

async function addRelation() {
  const title = query.value.trim()
  if (!title || saving.value || (!selected.value && (results.value.length || searching.value))) return
  saving.value = true
  try {
    if (!selected.value && relationType.value === "Partner") return
    const entity = selected.value || await post("/tasks", { task: { title, partner_id: props.partnerId } })
    await post("/crm_links", { crm_link: { source_type: "Partner", source_id: props.partnerId, target_type: relationType.value, target_id: entity.id, kind: kind.value } })
    query.value = ""
    results.value = []
    selected.value = null
    emit("refresh")
  } finally {
    saving.value = false
  }
}

async function remove(linkId) {
  await api(`/crm_links/${linkId}`, { method: "DELETE" })
  emit("refresh")
}
</script>

<template>
  <div class="partner-relations">
    <div class="relation-groups">
      <section class="relation-group"><header><div><span class="relation-icon partner">P</span><strong>Партнёры</strong></div><small>{{ partnerRows.length }}</small></header><div class="relation-list">
        <div v-for="row in partnerRows" :key="row.key" class="relation-row">
          <span class="relation-type partner">{{ row.label }}</span><RouterLink :to="row.path">{{ row.title }}</RouterLink><small>{{ row.subtitle || "Без категории" }}</small><button type="button" title="Удалить связь" @click="remove(row.linkId)">×</button>
        </div><div v-if="!partnerRows.length" class="empty-related-tasks">Связанных партнёров нет</div>
      </div></section>
      <section class="relation-group"><header><div><span class="relation-icon task">✓</span><strong>Задачи</strong></div><small>{{ taskRows.length }}</small></header><div class="relation-list">
        <div v-for="row in taskRows" :key="row.key" class="relation-row">
          <span class="relation-type task">Задача</span><RouterLink :to="row.path">{{ row.title }}</RouterLink><small>{{ row.subtitle }}</small><span></span>
        </div><div v-if="!taskRows.length" class="empty-related-tasks">Связанных задач нет</div>
      </div>
      </section>
    </div>
    <div class="relation-create"><div class="relation-type-tabs"><button type="button" :class="{active:relationType==='CrmTask'}" @click="changeType('CrmTask')">Связать задачу</button><button type="button" :class="{active:relationType==='Partner'}" @click="changeType('Partner')">Связать партнёра</button></div><form class="relation-picker" @submit.prevent="addRelation">
      <select v-model="kind" aria-label="Тип связи"><option value="related">Связана с</option><option value="blocks">Блокирует</option></select>
      <div class="entity-search">
        <input v-model="query" :placeholder="relationType === 'CrmTask' ? 'Найти или создать задачу…' : 'Найти партнёра…'" @input="scheduleSearch">
        <div v-if="results.length" class="entity-results">
          <button v-for="item in results" :key="`${item.type}-${item.id}`" type="button" @click="select(item)"><strong>{{ item.title }}</strong><small>{{ relationType === "CrmTask" ? "Задача" : "Партнёр" }} · {{ item.subtitle }}</small></button>
        </div>
      </div>
      <button class="primary" :disabled="!query.trim() || saving || searching || (!selected && (results.length || relationType === 'Partner'))">{{ saving ? "…" : selected ? "Связать" : results.length ? "Выберите" : relationType === "Partner" ? "Выберите партнёра" : "Создать задачу" }}</button>
    </form><small v-if="query && !selected && !results.length && !searching" class="create-task-hint">{{ relationType === "CrmTask" ? "Задача не найдена — будет создана с таким названием" : "Партнёр не найден" }}</small></div>
  </div>
</template>
